import type { InventoryTransferLineEvent } from '../../schemas/apiResponse.schemas';

export interface TransferChangeHistoryRow {
  readonly id: string;
  readonly name: string;
  readonly change: string;
  readonly actorName: string;
  readonly createdAt: string;
}

const MERGE_WINDOW_MS = 5000;

interface MergeLink {
  readonly role: 'source' | 'target';
  readonly partnerEventId: string;
}

function withinMergeWindow(left: string, right: string): boolean {
  const delta = Math.abs(Date.parse(left) - Date.parse(right));
  return Number.isFinite(delta) && delta <= MERGE_WINDOW_MS;
}

function findMerges(events: readonly InventoryTransferLineEvent[]): Map<string, MergeLink> {
  const links = new Map<string, MergeLink>();
  for (const event of events) {
    if (links.has(event.id)) continue;
    if (event.eventType !== 'quantity_changed') continue;
    if (event.newQuantity !== 0 || event.previousQuantity == null || event.previousQuantity <= 0) continue;
    const added = event.previousQuantity;
    const target = events.find((candidate) => (
      candidate.id !== event.id
      && !links.has(candidate.id)
      && candidate.lineId !== event.lineId
      && candidate.eventType === 'quantity_changed'
      && candidate.previousQuantity != null
      && candidate.newQuantity != null
      && candidate.newQuantity - candidate.previousQuantity === added
      && withinMergeWindow(candidate.createdAt, event.createdAt)
    ));
    if (!target) continue;
    links.set(event.id, { role: 'source', partnerEventId: target.id });
    links.set(target.id, { role: 'target', partnerEventId: event.id });
  }
  return links;
}

function eventName(event: InventoryTransferLineEvent): string {
  return event.eventType === 'quantity_changed' ? 'Quantity change' : 'Substitute SKU';
}

function eventChangeLabel(event: InventoryTransferLineEvent): string {
  if (event.eventType === 'quantity_changed') {
    return `${event.previousQuantity ?? '?'} → ${event.newQuantity ?? '?'}`;
  }
  return `${event.previousSku ?? '?'} → ${event.newSku ?? '?'}`;
}

function isScanPickMarker(event: InventoryTransferLineEvent): boolean {
  return event.eventType === 'quantity_changed'
    && event.previousQuantity != null
    && event.previousQuantity === event.newQuantity;
}

/** Newest change first. A merge onto this SKU is a Substitute SKU row, not a quantity change. */
export function transferChangeHistory(
  lineId: string,
  lineSku: string,
  events: readonly InventoryTransferLineEvent[],
): TransferChangeHistoryRow[] {
  const merges = findMerges(events);
  const rows: TransferChangeHistoryRow[] = [];
  for (const event of events) {
    if (event.lineId !== lineId || isScanPickMarker(event)) continue;
    const merge = merges.get(event.id);
    if (merge?.role === 'target') {
      const source = events.find((item) => item.id === merge.partnerEventId);
      const sourceSku = source?.sku?.trim() || '?';
      const targetSku = event.sku?.trim() || lineSku;
      rows.push({
        id: event.id,
        name: 'Substitute SKU',
        change: `${sourceSku} → ${targetSku}`,
        actorName: source?.actorName || event.actorName,
        createdAt: source?.createdAt || event.createdAt,
      });
      continue;
    }
    if (merge?.role === 'source') continue;
    rows.push({
      id: event.id,
      name: eventName(event),
      change: eventChangeLabel(event),
      actorName: event.actorName,
      createdAt: event.createdAt,
    });
  }
  return rows.sort((left, right) => {
    if (left.createdAt === right.createdAt) return 0;
    return left.createdAt > right.createdAt ? -1 : 1;
  });
}
